import { categoryMatches } from '../../lib/categoryTree.ts';
import {
  LIBRARY_FILTER_AXES,
  UNCATEGORISED,
  type LibraryFilterAxis,
  type LibraryFilterState,
  type LibraryInsertedFilter,
  type LibraryGroupMode,
  type LibrarySort,
  type LibraryTypeFilter,
  type LibraryUsedFilter,
} from './libraryFilterState.ts';
import type { CategoryTreeNode } from '../../lib/categoryTree.ts';
import type { LibraryItem } from '../../../shared/api.ts';

/**
 * The Library's filter row, its chips and its counts — as data.
 *
 * The same three jobs `routes/dashboard/filterOptions.ts`, `activeFilterChips.ts`
 * and `deckCountsLine.ts` do for the decks page, in one file because the
 * Library's axes are four rather than six and each of them is a page long only
 * on the decks side.
 *
 * ONE DEPARTURE FROM THE DECKS FILE, and it is the good half of that file's
 * design note. There, the per-axis matchers are a deliberate DUPLICATE of the
 * grid's own rule, pinned together by a test, because the real filter lives
 * inside one derivation over all six axes at once and the counts need the axes
 * separately. Here the matchers below are the ONLY copy: `useLibraryFilters`
 * imports `matchesLibraryItem` rather than restating it, so there is nothing
 * for a count to drift away from.
 *
 * COUNTS ARE CROSS-FILTERED, exactly as on the decks page: an option's count is
 * how many items the body will show if you pick it, leaving every other axis
 * alone. So Series inside a category counts that category's series. Absolute
 * counts read as a promise the body then breaks.
 */

/** One row of a pill's menu. `count` is absent where counting is meaningless
 *  (Group, Sort). */
export type LibraryFilterOption<T> = {
  value: T;
  label: string;
  count?: number;
  disabled?: boolean;
};

/** One pill: what it is called, what it currently reads, and what it offers. */
export type LibraryPillModel<T> = {
  axis: 'type' | 'category' | 'used' | 'group' | 'sort';
  label: string;
  value: T;
  valueLabel: string;
  options: LibraryFilterOption<T>[];
};

export type LibraryFilterBarModel = {
  type: LibraryPillModel<LibraryTypeFilter>;
  category: LibraryPillModel<string | null>;
  used: LibraryPillModel<LibraryUsedFilter>;
  group: LibraryPillModel<LibraryGroupMode>;
  sort: LibraryPillModel<LibrarySort>;
};

/**
 * What the free-text search reads for one item, already lowercased.
 *
 * The route builds it once per item from the payload: the item's NAME, its
 * CATEGORY path, and the TITLES OF ITS SLIDES — which is the only text on a
 * slide the listing carries (`LibrarySlide` has `title` and `sectionName`; the
 * slide's body lives in the bank deck's source, which this page never loads).
 * Searching the titles is what makes "morgan" find a case study whose item is
 * called something else.
 */
export type LibrarySearchText = (itemSlug: string) => string;

type MatchContext = {
  slideText: LibrarySearchText;
  /** Injectable clock for the inserted window. Absent means now. */
  now?: number;
};

/** An item is a SERIES once it holds more than one slide — the single fact the
 *  type axis, the badge and the menu's wording all read. */
export function isSeries(item: LibraryItem): boolean {
  return item.count > 1;
}

/**
 * How long "recently inserted" reaches back.
 *
 * Thirty days, where the decks page's Recently opened is a fortnight, and the
 * difference is deliberate: a deck is opened whenever someone works on it,
 * while a library item is inserted only when a new deck happens to need it. A
 * fortnight would leave the row reading 0 on a shelf that is being used
 * perfectly well, which is the one thing a counted row must not do.
 */
export const RECENTLY_INSERTED_DAYS = 30;

/** The earliest `lastInsertedAt` still inside the window, or null for any
 *  time. Injectable clock, so a test can assert on a window without pinning
 *  today. */
export function insertedWindowStart(window: LibraryInsertedFilter, now: number): number | null {
  if (window === null) return null;
  return now - RECENTLY_INSERTED_DAYS * 24 * 60 * 60 * 1000;
}

/** How many DECKS hold a copy. Absent on a client cached across the Run 8
 *  deploy, and 0 is the honest answer for an item nobody has used. */
function usedInDecks(item: LibraryItem): number {
  return item.usedInDecks ?? 0;
}

const AXIS_MATCHERS: Record<
  LibraryFilterAxis,
  (item: LibraryItem, filters: LibraryFilterState, ctx: MatchContext) => boolean
> = {
  query: (it, f, ctx) => {
    const q = f.query.trim().toLowerCase();
    if (!q) return true;
    return (
      it.name.toLowerCase().includes(q) ||
      (it.category || '').toLowerCase().includes(q) ||
      ctx.slideText(it.slug).includes(q)
    );
  },
  type: (it, f) => (f.type === null ? true : f.type === 'series' ? isSeries(it) : !isSeries(it)),
  category: (it, f) => {
    if (f.category === null) return true;
    // Library items always carry a category, so the Uncategorised bucket is
    // empty by construction rather than by accident — it is still ASKED
    // properly, because an item saved without one would otherwise vanish.
    if (f.category === UNCATEGORISED) return !(it.category || '').trim();
    return categoryMatches(it.category, f.category);
  },
  used: (it, f) => (f.used === null ? true : f.used === 'used' ? usedInDecks(it) > 0 : usedInDecks(it) === 0),
  inserted: (it, f, ctx) => {
    const since = insertedWindowStart(f.inserted, ctx.now ?? Date.now());
    if (since === null) return true;
    return typeof it.lastInsertedAt === 'number' && it.lastInsertedAt >= since;
  },
};

/** The items the body would show for `filters`. */
export function matchesLibraryItem(item: LibraryItem, filters: LibraryFilterState, ctx: MatchContext): boolean {
  return LIBRARY_FILTER_AXES.every((axis) => AXIS_MATCHERS[axis](item, filters, ctx));
}

/** The items left once every axis EXCEPT `axis` has been applied — the pool an
 *  axis's own menu counts against, which is what makes a count a preview of
 *  the click rather than a fact about the shelf. */
function poolFor(input: LibraryCountInput, axis: LibraryFilterAxis): LibraryItem[] {
  const others = LIBRARY_FILTER_AXES.filter((a) => a !== axis);
  return input.items.filter((it) => others.every((a) => AXIS_MATCHERS[a](it, input.filters, input)));
}

/** A choice leading to an empty body is offered but not clickable — except the
 *  one already chosen, which must stay selectable so the menu can show it. */
function disabledAt(count: number, selected: boolean): boolean | undefined {
  return count === 0 && !selected ? true : undefined;
}

export type LibraryCountInput = {
  items: readonly LibraryItem[];
  filters: LibraryFilterState;
  slideText: LibrarySearchText;
  /** Injectable clock, threaded through to the inserted matcher. */
  now?: number;
};

// ---------------------------------------------------------------------------
// The five pills
// ---------------------------------------------------------------------------

const TYPE_LABELS: Record<Exclude<LibraryTypeFilter, null>, string> = { single: 'Single', series: 'Series' };
const USED_LABELS: Record<Exclude<LibraryUsedFilter, null>, string> = { used: 'Used', never: 'Never used' };
const GROUP_LABELS: Record<LibraryGroupMode, string> = { category: 'Category', none: 'None' };
const SORT_LABELS: Record<LibrarySort, string> = {
  saved: 'Recently saved',
  inserted: 'Recently inserted',
  used: 'Most used',
  name: 'Name',
};

export function typeValueLabel(type: LibraryTypeFilter): string {
  return type ? TYPE_LABELS[type] : 'All';
}

export function typeOptions(input: LibraryCountInput): LibraryFilterOption<LibraryTypeFilter>[] {
  const pool = poolFor(input, 'type');
  const series = pool.filter(isSeries).length;
  const single = pool.length - series;
  const { type } = input.filters;
  return [
    { value: null, label: 'All', count: pool.length },
    { value: 'single', label: TYPE_LABELS.single, count: single, disabled: disabledAt(single, type === 'single') },
    { value: 'series', label: TYPE_LABELS.series, count: series, disabled: disabledAt(series, type === 'series') },
  ];
}

/** A child category's menu label: the tree gives children their leaf name,
 *  which is ambiguous once the row is out of its indentation. */
export function categoryMenuLabel(path: string): string {
  return path.replace('/', ' › ');
}

/** What the Category pill reads: the leaf, because the pill is narrow and the
 *  parent is usually obvious from the rail beside it. */
export function categoryValueLabel(category: string | null): string {
  if (category === null) return 'All';
  if (category === UNCATEGORISED) return 'Uncategorised';
  const slash = category.indexOf('/');
  return slash === -1 ? category : category.slice(slash + 1);
}

export function categoryOptions(
  input: LibraryCountInput,
  categoryTree: readonly CategoryTreeNode[],
): LibraryFilterOption<string | null>[] {
  const pool = poolFor(input, 'category');
  const active = input.filters.category;
  const countIn = (path: string) => pool.filter((it) => categoryMatches(it.category, path)).length;
  const options: LibraryFilterOption<string | null>[] = [{ value: null, label: 'All', count: pool.length }];
  for (const parent of categoryTree) {
    const count = countIn(parent.path);
    options.push({
      value: parent.path,
      label: parent.label,
      count,
      disabled: disabledAt(count, active === parent.path),
    });
    for (const child of parent.children) {
      const childCount = countIn(child.path);
      options.push({
        value: child.path,
        label: categoryMenuLabel(child.path),
        count: childCount,
        disabled: disabledAt(childCount, active === child.path),
      });
    }
  }
  return options;
}

export function usedValueLabel(used: LibraryUsedFilter): string {
  return used ? USED_LABELS[used] : 'Any deck';
}

export function usedOptions(input: LibraryCountInput): LibraryFilterOption<LibraryUsedFilter>[] {
  const pool = poolFor(input, 'used');
  const used = pool.filter((it) => usedInDecks(it) > 0).length;
  const never = pool.length - used;
  const current = input.filters.used;
  return [
    { value: null, label: 'Any deck', count: pool.length },
    { value: 'used', label: USED_LABELS.used, count: used, disabled: disabledAt(used, current === 'used') },
    { value: 'never', label: USED_LABELS.never, count: never, disabled: disabledAt(never, current === 'never') },
  ];
}

export function groupValueLabel(group: LibraryGroupMode): string {
  return GROUP_LABELS[group];
}

/** No counts: grouping does not change WHICH items are shown, so a number
 *  beside either row would be the same number twice. */
export function groupOptions(): LibraryFilterOption<LibraryGroupMode>[] {
  return [
    { value: 'category', label: GROUP_LABELS.category },
    { value: 'none', label: GROUP_LABELS.none },
  ];
}

export function sortValueLabel(sort: LibrarySort): string {
  return SORT_LABELS[sort];
}

export function sortOptions(): LibraryFilterOption<LibrarySort>[] {
  return (['saved', 'inserted', 'used', 'name'] as const).map((value) => ({ value, label: SORT_LABELS[value] }));
}

export type LibraryFilterBarInput = LibraryCountInput & {
  /** The rail's tree, for the Category menu's rows and their order. Counts are
   *  recomputed here rather than reused: the tree's are absolute. */
  categoryTree: readonly CategoryTreeNode[];
};

export function libraryFilterBarModel(input: LibraryFilterBarInput): LibraryFilterBarModel {
  const { filters } = input;
  return {
    type: {
      axis: 'type',
      label: 'Type',
      value: filters.type,
      valueLabel: typeValueLabel(filters.type),
      options: typeOptions(input),
    },
    category: {
      axis: 'category',
      label: 'Category',
      value: filters.category,
      valueLabel: categoryValueLabel(filters.category),
      options: categoryOptions(input, input.categoryTree),
    },
    used: {
      axis: 'used',
      label: 'Used in',
      value: filters.used,
      valueLabel: usedValueLabel(filters.used),
      options: usedOptions(input),
    },
    group: {
      axis: 'group',
      label: 'Group',
      value: filters.group,
      valueLabel: groupValueLabel(filters.group),
      options: groupOptions(),
    },
    sort: {
      axis: 'sort',
      label: 'Sort',
      value: filters.sort,
      valueLabel: sortValueLabel(filters.sort),
      options: sortOptions(),
    },
  };
}

// ---------------------------------------------------------------------------
// Ordering
// ---------------------------------------------------------------------------

/**
 * The orders, each falling back to the name so the body never reshuffles
 * between two renders of the same data. `savedAt` is always there now (an item
 * owns a deck, and this is that deck's last edit); `inserts` and
 * `lastInsertedAt` are missing on a studio whose usage registry was written
 * after the fact, and treat missing as the bottom of the list rather than as
 * the top.
 */
export const LIBRARY_SORT_COMPARATORS: Record<LibrarySort, (a: LibraryItem, b: LibraryItem) => number> = {
  saved: (a, b) => b.savedAt - a.savedAt || a.name.localeCompare(b.name),
  inserted: (a, b) => (b.lastInsertedAt ?? 0) - (a.lastInsertedAt ?? 0) || a.name.localeCompare(b.name),
  used: (a, b) => (b.inserts ?? 0) - (a.inserts ?? 0) || a.name.localeCompare(b.name),
  name: (a, b) => a.name.localeCompare(b.name),
};

// ---------------------------------------------------------------------------
// The chips, and the two count lines
// ---------------------------------------------------------------------------

export type LibraryChip = {
  axis: LibraryFilterAxis;
  /** The axis name, ending in a colon: "Type:". */
  label: string;
  /** What it is set to, in the user's words. */
  value: string;
  /** No library chip carries a tint: the Library's category registry is a bare
   *  `string[]`, so its rows have no colour to match. Kept in the shape so the
   *  row component is the decks page's, not a second one. */
  dot: null;
  /** What the × button announces it will do. */
  clearLabel: string;
};

/** One chip per set filter, structural first and the free text last — the
 *  order the decks page's row uses. */
export function libraryFilterChips(filters: LibraryFilterState): LibraryChip[] {
  const chips: LibraryChip[] = [];
  if (filters.type) {
    const value = TYPE_LABELS[filters.type];
    chips.push({ axis: 'type', label: 'Type:', value, dot: null, clearLabel: `Clear the ${value} type filter` });
  }
  if (filters.category) {
    const value = categoryValueLabel(filters.category);
    chips.push({
      axis: 'category',
      label: 'Category:',
      value,
      dot: null,
      clearLabel: `Clear the ${value} category filter`,
    });
  }
  if (filters.used) {
    const value = USED_LABELS[filters.used];
    chips.push({ axis: 'used', label: 'Used in:', value, dot: null, clearLabel: `Clear the ${value} usage filter` });
  }
  if (filters.inserted) {
    chips.push({
      axis: 'inserted',
      label: 'Inserted:',
      value: `Last ${RECENTLY_INSERTED_DAYS} days`,
      dot: null,
      clearLabel: 'Clear the recently inserted filter',
    });
  }
  if (filters.query) {
    chips.push({
      axis: 'query',
      label: 'Search:',
      value: filters.query,
      dot: null,
      clearLabel: `Clear the search for ${filters.query}`,
    });
  }
  return chips;
}

/** "Showing 12 of 22 items" — the filtered count, which the title row's
 *  standing counts deliberately do not carry. */
export function libraryShowingLine(shown: number, total: number): string {
  return `Showing ${shown} of ${total} ${total === 1 ? 'item' : 'items'}`;
}

export type LibraryCounts = { items: number; slides: number; series: number };

/** The counts for the sub line, off the raw payload. */
export function libraryCounts(items: readonly LibraryItem[]): LibraryCounts {
  return {
    items: items.length,
    slides: items.reduce((n, it) => n + (it.count || 0), 0),
    series: items.filter(isSeries).length,
  };
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * The sentence under the "Library" title: "22 items · 41 saved slides ·
 * 6 series". It describes the WHOLE shelf and never the filtered subset —
 * same split as the decks page, where "how many am I looking at right now" is
 * the chip row's job.
 */
export function libraryCountsLine({ items, slides, series }: LibraryCounts): string {
  return `${plural(items, 'item', 'items')} · ${plural(slides, 'saved slide', 'saved slides')} · ${plural(series, 'series', 'series')}`;
}
